import { injectable, interfaces } from 'inversify';
import { action, IObservableArray, makeObservable, observable } from 'mobx';
import { PaginationInfoDefault } from '../../models/paginationInfo.model';

export enum AttachmentType {
  FOLDER = 10,
  FILE = 20,
  IMAGE_FILE = 30,
}

export interface IAttachment {
  id: string;
  name: string;
  createdDate: string;
  modifiedDate: string;
  author: string;
  parentId: string;
  type: AttachmentType;
  items: number;
  size: number;
}

export interface IAttachmentBreadcrumb {
  id: string;
  name: string;
}

interface ISelectedAttachments {
  ids: string[];
  isAllSelected: boolean;
}

interface IAttachmentSorting {
  orderBy?: string;
  orderType?: string;
  forId: string;
}

interface IAttachmentPaging {
  forId: string;
  pageNumber: number;
  pagesCount: number;
  totalCount: number;
  pageSize: number;
}

interface IAttachmentsPagingWithSorting extends IAttachmentPaging, IAttachmentSorting {}

export interface IAttachmentsStore {
  readonly attachments: IObservableArray<IAttachment>;
  attachmentsPaging: IAttachmentPaging;
  readonly attachmentsSorting: IObservableArray<IAttachmentSorting>;
  readonly breadcrumbs: IObservableArray<IAttachmentBreadcrumb>;
  selectedAttachments: ISelectedAttachments;
  showDeleteConfirmationModal: boolean;
  isDeleteRequestInProgress: boolean;
  clearSelectedAttachments(): void;
  getPaging(workspaceId: string, attachmentId: string | undefined): IAttachmentsPagingWithSorting;
  updatePaging(paging: IAttachmentsPagingWithSorting): void;
}

export namespace IAttachmentsStore {
  export const $: interfaces.ServiceIdentifier<IAttachmentsStore> = Symbol('ATTACHMENTS_STORE');
}

export const defaultAttachmentsPaging: IAttachmentPaging = {
  ...PaginationInfoDefault,
  forId: '',
};

const defaultSorting: IAttachmentSorting = {
  orderBy: 'name',
  orderType: 'asc',
  forId: '',
};

@injectable()
export class AttachmentsStore implements IAttachmentsStore {
  attachments: IObservableArray<IAttachment> = observable.array([]);

  attachmentsPaging: IAttachmentPaging = defaultAttachmentsPaging;

  attachmentsSorting: IObservableArray<IAttachmentSorting> = observable.array([]);

  breadcrumbs: IObservableArray<IAttachmentBreadcrumb> = observable.array([]);

  selectedAttachments: ISelectedAttachments = {
    ids: [],
    isAllSelected: false,
  };

  showDeleteConfirmationModal: boolean = false;

  isDeleteRequestInProgress: boolean = false;

  constructor() {
    makeObservable(this, {
      attachments: observable,
      attachmentsPaging: observable,
      attachmentsSorting: observable,
      breadcrumbs: observable,
      selectedAttachments: observable,
      showDeleteConfirmationModal: observable,
      isDeleteRequestInProgress: observable,
      updatePaging: action,
    });
  }

  clearSelectedAttachments(): void {
    this.selectedAttachments.isAllSelected = false;
    this.selectedAttachments.ids = [];
  }

  getPaging(workspaceId: string, attachmentId: string | undefined): IAttachmentsPagingWithSorting {
    const id = attachmentId ?? workspaceId;

    const paging = this.attachmentsPaging.forId === id ? this.attachmentsPaging : defaultAttachmentsPaging;
    let sorting = this.attachmentsSorting.find((x) => x.forId === id);
    if (!sorting) {
      sorting = { ...defaultSorting };
    }

    return { ...paging, ...sorting, forId: id };
  }

  updatePaging(paging: IAttachmentsPagingWithSorting): void {
    this.attachmentsPaging = paging;

    const index = this.attachmentsSorting.findIndex((x) => x.forId === paging.forId);
    if (index > -1) {
      this.attachmentsSorting.splice(index, 1);
    }
    this.attachmentsSorting.push({ forId: paging.forId, orderBy: paging.orderBy, orderType: paging.orderType });
  }
}
